<script setup lang="ts">
import { ref } from 'vue';

import { WIDGET_ATTACHMENT_MIME_TYPES } from '@support/widget-contracts';

defineProps<{
  disabled?: boolean;
  /** The button's test id; the file input behind it is `<testId>-input`. */
  testId: string;
}>();

const emit = defineEmits<{
  pick: [files: File[]];
}>();

const input = ref<HTMLInputElement | null>(null);

const onChange = (): void => {
  const files = [...(input.value?.files ?? [])];

  // Reset, so choosing the same file again after removing it still fires.
  if (input.value) {
    input.value.value = '';
  }

  if (files.length > 0) {
    emit('pick', files);
  }
};
</script>

<template>
  <button
    type="button"
    class="attach"
    aria-label="Attach an image"
    title="Attach an image"
    :disabled="disabled"
    :data-testid="testId"
    @click="input?.click()"
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>
  <input
    ref="input"
    type="file"
    hidden
    multiple
    :accept="WIDGET_ATTACHMENT_MIME_TYPES.join(',')"
    :data-testid="`${testId}-input`"
    @change="onChange"
  >
</template>

<style scoped>
.attach {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--w-border);
  border-radius: 8px;
  background: var(--w-bg);
  color: var(--w-text-muted);
}

.attach:hover:not(:disabled) {
  color: var(--w-text);
}

.attach:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
